export const rearCameraConstraints: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: "environment" },
  },
};

export type CameraState = "denied" | "error";

export type CameraFailure = {
  state: CameraState;
  message: string;
};

const cameraErrorName = (error: unknown) => {
  if (typeof error !== "object" || error === null || !("name" in error)) return "";
  return typeof error.name === "string" ? error.name : "";
};

export const canRetryWithAutomaticCamera = (error: unknown) =>
  ["NotFoundError", "DevicesNotFoundError", "OverconstrainedError", "ConstraintNotSatisfiedError"].includes(
    cameraErrorName(error),
  );

export const describeCameraFailure = (error: unknown, manualAction: string): CameraFailure => {
  const name = cameraErrorName(error);

  if (["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(name)) {
    return {
      state: "denied",
      message: `Camera access is blocked. Allow camera permission for Mero Perks in your browser settings, then try again. ${manualAction}`,
    };
  }

  if (["NotReadableError", "TrackStartError", "AbortError"].includes(name)) {
    return {
      state: "error",
      message: `A camera was detected but could not be started. Close any other app using the camera, then try again. ${manualAction}`,
    };
  }

  if (["NotFoundError", "DevicesNotFoundError", "OverconstrainedError", "ConstraintNotSatisfiedError"].includes(name)) {
    return {
      state: "error",
      message: `The browser could not open an available camera. Check this site's camera permission and try again. ${manualAction}`,
    };
  }

  if (typeof window !== "undefined" && !window.isSecureContext) {
    return {
      state: "error",
      message: `Camera access requires a secure HTTPS connection. ${manualAction}`,
    };
  }

  return {
    state: "error",
    message: `The camera could not be started. Check camera permission and try again. ${manualAction}`,
  };
};

export const cameraOptions = (devices: MediaDeviceInfo[]) =>
  devices.map((device, index) => ({
    deviceId: device.deviceId,
    label: device.label || `Camera ${String(index + 1)}`,
  }));

export const activeCameraDeviceId = (video: HTMLVideoElement) => {
  const stream = video.srcObject as MediaStream | null;
  if (!stream || typeof stream.getVideoTracks !== "function") return "";
  const track = stream.getVideoTracks()[0];
  if (!track || typeof track.getSettings !== "function") return "";
  return track.getSettings().deviceId ?? "";
};
